import React from 'react';

import { useTranslation } from '@/i18n';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Selector } from '@/components/ui/inputs/selector';
import { useWorkingDayFormPanel } from '@/hooks/shift/useWorkingDayFormPanel';
import type { WorkingDayFormRecord } from '@/hooks/shift/useWorkingDayFormPanel';


interface WorkingDayFormPanelProps {
  onSubmit?: () => void;
  readOnly?: boolean;
  record?: WorkingDayFormRecord;
}

export const WorkingDayFormPanel: React.FC<WorkingDayFormPanelProps> = ({
  onSubmit,
  readOnly,
  record,
}) => {
  const { t: translate } = useTranslation();

  const {
    nameValue,
    workingDaysValue,
    offDaysValue,
    workingDayTypeValue,
    workingDayTypeOptions,
    isEditMode,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    errors,
    isSubmitted,
    handleNameChange,
    handleWorkingDaysChange,
    handleOffDaysChange,
    handleWorkingDayTypeChange,
    handleSubmit,
  } = useWorkingDayFormPanel({ onSubmit, readOnly, record });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="working-day-form-panel">
      <SidePanelFormLayout.Fields>
        {isEditMode && record && (
          <LabelInput
            label={translate('schedule:workingDay.columns.id')}
            value={String(record.id)}
            disabled
          />
        )}

        <LabelInput
          label={translate('schedule:workingDay.form.fields.name')}
          value={nameValue}
          onChange={handleNameChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.name && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.name?.message && isSubmitted
              ? translate(String(errors.name.message))
              : undefined
          }
          data-testid="working-day-name-input"
        />

        <LabelInput
          label={translate('schedule:workingDay.form.fields.workingDays')}
          value={workingDaysValue}
          onChange={handleWorkingDaysChange}
          type="number"
          required
          maxLength={3}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.workingDays && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.workingDays?.message && isSubmitted
              ? translate(String(errors.workingDays.message))
              : undefined
          }
          data-testid="working-day-working-days-input"
        />

        <LabelInput
          label={translate('schedule:workingDay.form.fields.offDays')}
          value={offDaysValue}
          onChange={handleOffDaysChange}
          type="number"
          required
          maxLength={3}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.offDays && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.offDays?.message && isSubmitted
              ? translate(String(errors.offDays.message))
              : undefined
          }
          data-testid="working-day-off-days-input"
        />

        <Selector
          label={translate('schedule:workingDay.form.fields.workingDayType')}
          required
          options={workingDayTypeOptions}
          value={workingDayTypeValue}
          onChange={handleWorkingDayTypeChange}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.workingDayType && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.workingDayType?.message && isSubmitted
              ? translate(String(errors.workingDayType.message))
              : undefined
          }
          data-testid="working-day-type-selector"
        />
      </SidePanelFormLayout.Fields>

      {!effectiveReadOnly && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard
            action={isEditMode ? 'edit' : 'create'}
          >
            <PrimaryButton
              type="submit"
              fullWidth
              isLoading={isSubmitting}
              disabled={!isReady || isSubmitting}
            >
              {translate(
                isEditMode
                  ? 'schedule:workingDay.messages.submitEdit'
                  : 'schedule:workingDay.messages.submitCreate',
              )}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default WorkingDayFormPanel;
